// HistoryModal.js - Clean and User-Friendly Design with Statistics Update
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import API_URL from '../../../config';
import './HistoryModal.css';

function HistoryModal({ isOpen, onClose, contentId, contentTitle }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [updatingStats, setUpdatingStats] = useState({});
    const [bulkUpdating, setBulkUpdating] = useState(false);
    const [lastUpdateTimes, setLastUpdateTimes] = useState({});

    useEffect(() => {
        if (isOpen && contentId) {
            fetchHistory();
        }
    }, [isOpen, contentId]);

    const fetchHistory = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/content-library/${contentId}/history`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.data.success) {
                const historyData = response.data.history || [];
                setHistory(historyData);
                
                // Son güncelleme zamanlarını kaydet
                const updateTimes = {};
                historyData.forEach(item => {
                    if (item.stats_updated_at) {
                        updateTimes[item.id] = new Date(item.stats_updated_at);
                    }
                });
                setLastUpdateTimes(updateTimes);
            } else {
                setError('Veriler yüklenemedi');
                setHistory([]);
            }
        } catch (error) {
            console.error('History fetch error:', error);
            setError('Bağlantı hatası oluştu');
            setHistory([]);
        } finally {
            setLoading(false);
        }
    };

        const updatePostStatistics = async (postId) => {
            setUpdatingStats(prev => ({ ...prev, [postId]: true }));
            
            try {
                const token = localStorage.getItem('token');
                const response = await axios.put(
                    `${API_URL}/content-library/post/${postId}/statistics`,
                    {},
                    {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }
                );
                
                if (response.data.success) {
                    // History listesini güncelle
                    setHistory(prevHistory => 
                        prevHistory.map(item => 
                            item.id === postId 
                                ? {
                                    ...item,
                                    views: response.data.data.views,
                                    reactions: response.data.data.reactions,
                                    comments: response.data.data.comments,
                                    forwards: response.data.data.forwards,
                                    stats_updated_at: response.data.data.lastUpdated
                                }
                                : item
                        )
                    );
                    
                    // Son güncelleme zamanını kaydet
                    setLastUpdateTimes(prev => ({
                        ...prev,
                        [postId]: new Date(response.data.data.lastUpdated)
                    }));
                    
                    // Başarı bildirimi
                    toast.success('İstatistikler güncellendi');
                }
            } catch (err) {
                if (err.response?.status === 429) {
                    const remainingTime = err.response.data.remainingTime;
                    toast.error(`Lütfen ${remainingTime} saniye sonra tekrar deneyin`, {
                        duration: 5000
                    });
                } else {
                    console.error('İstatistik güncelleme hatası:', err);
                    toast.error('İstatistikler güncellenemedi');
                }
            } finally {
                setUpdatingStats(prev => ({ ...prev, [postId]: false }));
            }
        };

            const updateAllStatistics = async () => {
            setBulkUpdating(true);
            
            try {
                const token = localStorage.getItem('token');
                const response = await axios.put(
                    `${API_URL}/content-library/${contentId}/statistics/update`,
                    {},
                    {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }
                );
                
                if (response.data.success) {
                    toast.success(`${response.data.data.updated} post güncellendi`);
                    // Listeyi yenile
                    await fetchHistory();
                }
            } catch (err) {
                console.error('Toplu güncelleme hatası:', err);
                toast.error('Toplu güncelleme başarısız');
            } finally {
                setBulkUpdating(false);
            }
        };

    const canUpdateStats = (postId) => {
        const lastUpdate = lastUpdateTimes[postId];
        if (!lastUpdate) return true;
        
        const now = new Date();
        const diffMinutes = (now - lastUpdate) / 1000 / 60;
        return diffMinutes >= 5; // 5 dakika geçmişse güncellemeye izin ver
    };

    const formatNumber = (num) => {
        if (!num || num === 0) return '0';
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num.toLocaleString('tr-TR');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Henüz gönderilmedi';
        
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffHours = Math.abs(now - date) / (1000 * 60 * 60);
            
            if (diffHours < 1) {
                return 'Az önce';
            } else if (diffHours < 24) {
                return `${Math.floor(diffHours)} saat önce`;
            } else if (diffHours < 168) {
                return `${Math.floor(diffHours / 24)} gün önce`;
            } else {
                return date.toLocaleDateString('tr-TR');
            }
        } catch {
            return 'Geçersiz tarih';
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'sent':
                return { text: 'Gönderildi', className: 'status-success' };
            case 'failed':
                return { text: 'Başarısız', className: 'status-error' };
            case 'pending':
                return { text: 'Bekliyor', className: 'status-pending' };
            default:
                return { text: 'Bilinmiyor', className: 'status-unknown' };
        }
    };

    // Calculate totals
    const totalStats = {
        shares: history.length,
        views: history.reduce((sum, item) => sum + (item.views || 0), 0),
        reactions: history.reduce((sum, item) => sum + (item.reactions || 0), 0),
        forwards: history.reduce((sum, item) => sum + (item.forwards || 0), 0)
    };

    if (!isOpen) return null;

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                
                {/* Header */}
                <div className="modal-header">
                    <h2>Paylaşım Geçmişi</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                {/* Quick Stats */}
                {history.length > 0 && (
                    <div className="quick-stats">
                        <div className="quick-stat">
                            <span className="stat-number">{totalStats.shares}</span>
                            <span className="stat-text">Paylaşım</span>
                        </div>
                        <div className="quick-stat">
                            <span className="stat-number">{formatNumber(totalStats.views)}</span>
                            <span className="stat-text">Görüntülenme</span>
                        </div>
                        <div className="quick-stat">
                            <span className="stat-number">{formatNumber(totalStats.reactions)}</span>
                            <span className="stat-text">Beğeni</span>
                        </div>
                        <div className="quick-stat">
                            <span className="stat-number">{formatNumber(totalStats.forwards)}</span>
                            <span className="stat-text">İletim</span>
                        </div>
                    </div>
                )}

                {/* Update All Button */}
                {history.length > 0 && (
                    <div className="update-actions">
                        <button 
                            className="update-all-button"
                            onClick={updateAllStatistics}
                            disabled={bulkUpdating}
                        >
                            {bulkUpdating ? 'Güncelleniyor...' : 'Tüm İstatistikleri Güncelle'}
                        </button>
                    </div>
                )}

                {/* Content */}
                <div className="modal-body">
                    {loading ? (
                        <div className="state-container">
                            <div className="loading-spinner"></div>
                            <p>Yükleniyor...</p>
                        </div>
                    ) : error ? (
                        <div className="state-container">
                            <div className="error-icon">⚠</div>
                            <h3>Hata Oluştu</h3>
                            <p>{error}</p>
                            <button className="retry-button" onClick={fetchHistory}>
                                Tekrar Dene
                            </button>
                        </div>
                    ) : history.length === 0 ? (
                        <div className="state-container">
                            <div className="empty-icon">📭</div>
                            <h3>Henüz Paylaşım Yok</h3>
                            <p>Bu içerik hiçbir kanalda paylaşılmamış.</p>
                        </div>
                    ) : (
                        <div className="history-list">
                            {history.map((item) => {
                                const statusBadge = getStatusBadge(item.status);
                                const isUpdating = updatingStats[item.id];
                                const canUpdate = canUpdateStats(item.id) && item.telegram_message_id;
                                
                                return (
                                    <div key={item.id} className="history-card">
                                        <div className="card-header">
                                            <div className="channel-info">
                                                <h4>{item.channel_name || 'Bilinmeyen Kanal'}</h4>
                                                <span className={`status-badge ${statusBadge.className}`}>
                                                    {statusBadge.text}
                                                </span>
                                            </div>
                                            <div className="post-date">
                                                {formatDate(item.sent_at)}
                                            </div>
                                        </div>

                                        <div className="card-stats">
                                            <div className="stat-item">
                                                <span className="stat-value">{formatNumber(item.views || 0)}</span>
                                                <span className="stat-label">Görüntülenme</span>
                                            </div>
                                            <div className="stat-item">
                                                <span className="stat-value">{formatNumber(item.reactions || 0)}</span>
                                                <span className="stat-label">Beğeni</span>
                                            </div>
                                            <div className="stat-item">
                                                <span className="stat-value">{formatNumber(item.comments || 0)}</span>
                                                <span className="stat-label">Yorum</span>
                                            </div>
                                            <div className="stat-item">
                                                <span className="stat-value">{formatNumber(item.forwards || 0)}</span>
                                                <span className="stat-label">İletim</span>
                                            </div>
                                        </div>

                                        {/* Stats Update Info */}
                                        {item.stats_updated_at && (
                                            <div className="stats-update-info">
                                                <small>Son güncelleme: {formatDate(item.stats_updated_at)}</small>
                                            </div>
                                        )}

                                        <div className="card-actions">
                                            {item.telegram_link && (
                                                <a 
                                                    href={item.telegram_link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="telegram-link"
                                                >
                                                    Telegram'da Görüntüle
                                                </a>
                                            )}
                                            
                                            {item.telegram_message_id && (
                                                <button
                                                    className={`update-stats-button ${!canUpdate ? 'disabled' : ''}`}
                                                    onClick={() => updatePostStatistics(item.id)}
                                                    disabled={isUpdating || !canUpdate}
                                                    title={!canUpdate ? '5 dakika beklemelisiniz' : 'İstatistikleri güncelle'}
                                                >
                                                    {isUpdating ? 'Güncelleniyor...' : 'İstatistikleri Güncelle'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="modal-footer">
                    <button className="close-button" onClick={onClose}>
                        Kapat
                    </button>
                </div>
            </div>
        </div>
    );
}

export default HistoryModal;